import { HttpErrorResponse } from '@angular/common/http';

const DOWNLOAD_TIMEOUT_MESSAGE =
  'The export took too long or the connection dropped. Try a shorter date range.';

/**
 * Server errors arrive in two shapes: `{ message }` (global handler) or
 * `{ errors: { <field>: '<msg>' } }` (Joi validators; field is '' for object-level rules).
 */
function pickServerMessage(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const { message, errors } = parsed as { message?: unknown; errors?: unknown };
  const fieldErrors = errors && typeof errors === 'object' ? Object.values(errors) : [];
  const found = [message, ...fieldErrors].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  );
  return found ?? null;
}

/**
 * File downloads use `responseType: 'blob'`, so a JSON error body arrives as a Blob and its
 * message is lost (CWS-1637). Returns the server's message (4xx only — 5xx bodies may carry
 * internal details such as DB errors), a timeout hint, or null.
 */
export async function readDownloadErrorMessage(error: unknown): Promise<string | null> {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  if (error.status === 0) {
    return DOWNLOAD_TIMEOUT_MESSAGE;
  }
  if (error.status < 400 || error.status >= 500) {
    return null;
  }
  const body: unknown = error.error;
  try {
    const raw = body instanceof Blob ? await body.text() : body;
    return pickServerMessage(typeof raw === 'string' ? JSON.parse(raw) : raw);
  } catch {
    return null;
  }
}

/** Shows the readable download error, or `fallback` when none can be surfaced. */
export function notifyDownloadError(
  error: unknown,
  fallback: string,
  show: (detail: string) => void
): void {
  void readDownloadErrorMessage(error).then(message => show(message ?? fallback));
}
